import * as React from 'react';
import { object as PropObject } from 'prop-types';
import { Col, ControlLabel, Form, FormControl, FormGroup } from 'react-bootstrap';

import { Organization, Permission, User } from '../../gs-core/common';

import { ActionTypes, Doc, PermissionEvaluator } from '../../common/models';
import { OwningUnitSelect } from './OwningUnitSelect';
import { ServiceRegistry } from '../../services';

interface OwnerSectionProps {
  doc: Doc;
  editing: boolean;
  currentUser: User;
  permissions: Permission[];
  onChangeOwningUnit: (owningUnit: Organization) => void;
}

interface OwnerSectionState { owningUnits: Organization[]; }

export class OwnerSection extends React.Component<OwnerSectionProps, OwnerSectionState> {
  static contextTypes = { services: PropObject };

  constructor(props: OwnerSectionProps) {
    super(props);
    this.state = { owningUnits: [] };
  }

  async componentWillReceiveProps({ doc, currentUser, permissions }: OwnerSectionProps) {
    const { owningUnitService, errorLogger } = this.context.services as ServiceRegistry;

    try {
      if (doc.sameAccess(this.props.doc)) {
        return;
      }

      if (!currentUser.isUnder) {
        throw new Error('Current user must be a UserModel');
      }

      const permissionEvaluator = PermissionEvaluator.forUser(currentUser)
        .withPermissions(permissions);

      const editScopeGroups = permissionEvaluator.findScopeGroups(ActionTypes.EDIT);

      // If the user can edit within a scope group, find the subunits of that scope group
      const possibleOwningUnits = editScopeGroups.length ?
        await owningUnitService.findOwningUnitsUnder(editScopeGroups) :
        permissionEvaluator.findOwningUnits();

      // Filter down units can assign to this subject's groups
      const owningUnits = possibleOwningUnits
        .filter(unit =>
          permissionEvaluator
            .forDoc({ subject: doc.subject, owningUnit: unit })
            .canEdit()
        );

      // Put the doc.owningUnit into the dropdown if we're in another unit (AAS editing SFS)
      if (!owningUnits.some(unit => unit.id === doc.owningUnit.id)) {
        owningUnits.push(doc.owningUnit);
      }

      this.setState({ owningUnits });
    } catch (err) {
      await errorLogger.log(err);
    }
  }

  render() {
    const { owningUnits } = this.state;
    const { doc, editing, currentUser, onChangeOwningUnit } = this.props;

    return (
      <Form horizontal={true} className="Section">
        <FormGroup controlId="owingUnit">
          <Col xs={3} componentClass={ControlLabel}>Owner</Col>
          <Col xs={9}>
            {editing ?
              (
                <OwningUnitSelect
                  selectedOwningUnit={doc.owningUnit}
                  owningUnits={owningUnits}
                  onChange={onChangeOwningUnit}
                />
              ) :
              (<FormControl.Static>{doc.owningUnit.displayName || doc.owningUnit.name}</FormControl.Static>)
            }
          </Col>
        </FormGroup>
        <FormGroup controlId="lastModifiedby">
          <Col xs={3} componentClass={ControlLabel}>
            Modified
          </Col>
          <Col xs={9}>
            <FormControl.Static>
              {doc.modifiedDate.toDateString()} by {currentUser.id === doc.modifiedBy.id ? 'me' : doc.modifiedBy.name}
            </FormControl.Static>
          </Col>
        </FormGroup>
        <FormGroup controlId="createdBy">
          <Col xs={3} componentClass={ControlLabel}>
            Created
          </Col>
          <Col xs={9}>
            <FormControl.Static>
              {doc.createdDate.toDateString()} by {currentUser.id === doc.createdBy.id ? 'me' : doc.createdBy.name}
            </FormControl.Static>
          </Col>
        </FormGroup>
      </Form>
    );
  }
}



// WEBPACK FOOTER //
// ./src/DocView/Section/OwnerSection.tsx